import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Megaphone, Trash2, Plus } from 'lucide-react';

/**
 * 관리자: 보철갤러리 스폰서 슬롯 관리 (/admin/gallery-sponsors)
 * - 갤러리 상단 '광고' 슬롯에 노출할 기공소 지정 (현재 무료 운영, 추후 유료화)
 * - 후보: 홍보 등록(labAdvertisements)된 기공소
 */
const fmtOrder = (n) => `${n}번`;

function AdminGallerySponsors() {
    const [sponsors, setSponsors] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const [spSnap, adSnap] = await Promise.all([
                getDocs(collection(db, 'gallerySponsors')),
                getDocs(collection(db, 'labAdvertisements')),
            ]);
            const sp = spSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0));
            setSponsors(sp);
            const spIds = new Set(sp.map(s => s.labId));
            setCandidates(adSnap.docs
                .map(d => {
                    const a = d.data();
                    return {
                        labId: d.id,
                        labName: a.businessName || a.companyName || a.labName || d.id,
                        photoUrl: (a.portfolioImages || [])[0] || a.heroImageUrl || a.profileImageUrl || '',
                    };
                })
                .filter(c => !spIds.has(c.labId) && c.labName));
        } catch (e) {
            console.error('스폰서 목록 조회 실패:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const addSponsor = async (c) => {
        if (sponsors.length >= 5 && !window.confirm('스폰서 슬롯은 5개 권장입니다. 그래도 추가할까요?')) return;
        setSaving(true);
        try {
            await setDoc(doc(db, 'gallerySponsors', c.labId), {
                labId: c.labId,
                labName: c.labName,
                photoUrl: c.photoUrl,
                order: sponsors.length + 1,
                active: true,
                paid: false,   // 현재 무료 운영 — 유료화 시 결제 연동
                createdAt: serverTimestamp(),
            });
            await fetchAll();
        } catch (e) {
            alert('추가 실패: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const removeSponsor = async (sp) => {
        if (!window.confirm(`${sp.labName}을(를) 스폰서에서 제외할까요?`)) return;
        setSaving(true);
        try {
            await deleteDoc(doc(db, 'gallerySponsors', sp.id));
            await fetchAll();
        } catch (e) {
            alert('제외 실패: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                <Megaphone size={24} color="#F59E0B" /> 갤러리 스폰서 관리
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20, lineHeight: 1.7 }}>
                보철갤러리 상단 '광고' 슬롯에 노출할 기공소를 지정합니다. 권장 슬롯 3~5개.
                현재는 무료 운영이며, 기공소 100곳 이상 시 유료 전환 예정입니다.
            </p>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>불러오는 중...</div>
            ) : (
                <>
                    <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>현재 스폰서 ({sponsors.length})</h2>
                    {sponsors.length === 0 ? (
                        <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12, marginBottom: 24 }}>지정된 스폰서가 없습니다.</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                            {sponsors.map(sp => (
                                <div key={sp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 12, padding: '10px 14px' }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#D97706', minWidth: 34 }}>{fmtOrder(sp.order || 0)}</span>
                                    {sp.photoUrl && <img src={sp.photoUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />}
                                    <span style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>{sp.labName}</span>
                                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>{sp.paid ? '유료' : '무료 운영'}</span>
                                    <button onClick={() => removeSponsor(sp)} disabled={saving}
                                        style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: 6 }}><Trash2 size={17} /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>추가 가능한 기공소</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {candidates.map(c => (
                            <div key={c.labId} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #E5E7EB', borderRadius: 12, padding: '10px 14px', background: '#fff' }}>
                                {c.photoUrl
                                    ? <img src={c.photoUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                                    : <div style={{ width: 48, height: 48, borderRadius: 8, background: '#F3F4F6' }} />}
                                <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{c.labName}</span>
                                <button onClick={() => addSponsor(c)} disabled={saving}
                                    style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#F59E0B', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                                    <Plus size={14} /> 스폰서 지정
                                </button>
                            </div>
                        ))}
                        {candidates.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>추가할 수 있는 기공소가 없습니다.</div>}
                    </div>
                </>
            )}
        </div>
    );
}

export default AdminGallerySponsors;
